/**
 * useConfirmCreate · 方案卡「确认创建」副作用收口
 *
 * 从 usePlanPreview 抽出，让 usePlanPreview 聚焦视图派生。
 * 职责：付费墙判定 → confirmCreate 埋点 → postConfirm 入队 → 写回 card 只读 + 追加任务卡 →
 *       422 exceeded 兜底弹开通窗。
 */

import { useCallback, useRef, useState } from 'react'
import { message } from 'antd'
import { useAppDispatch, usePayment } from '../../../../app/hooks'
import {
  assistantFoldersHydrated,
  makeBuildTaskStatusMessage,
  messageAppended
} from '../../../../slices/aiClassify'
import { postConfirm } from '../../services/sessionApi'
import {
  batchSourceDetail,
  batchUpgradeTarget
} from '../../../components/FolderPaywall/paywall'
import type {
  PaywallQuotaBody,
  PaywallView
} from '../../../components/FolderPaywall/paywall'
import { batchModalData } from '../../../components/FolderPaywall/paywallModalData'
import { folderPaywallCustomContent } from '../../../components/FolderPaywall/paywallModalContent'
import type { TierInfo } from '../../../components/FolderPaywall/folderTiers'
import { trackAIChatClick } from '../../utils/trackClassify'
import type { PlanFolder } from '../../../../types/ai-classify'

export interface UseConfirmCreateParams {
  /** 团队 UUID · postConfirm 必填，缺失时提前返回不调接口 */
  groupID: string
  /** 当前 AI 会话 ID · 确认创建绑定到特定 session */
  sessionId: string
  /** 方案卡所在 assistant 消息 ID · 写回 assistantFoldersHydrated 定位用 */
  messageId: string
  /** 当前方案文件夹树 · 写回 card 时随 excludedKeys 一并写入 store */
  folders: PlanFolder[]
  /** 本地反选 key 集合 · 空集合 = 全建 */
  localExcludedKeys: Set<string>
  /** 付费墙视图模型 · kind 非 none 时直接弹开通窗，不调接口 */
  paywall: PaywallView
  /** 是否可编辑 · false 时跳过（只读卡片禁用按钮后仍可能误调，防御） */
  canEdit: boolean
  /** 当前选中文件夹数 · 埋点 number1 字段 */
  selectedCount: number
  /** 当前会员档 vip_kind · 拼弹窗 customContent 用 */
  vipKind: number
  /** 团队已用文件夹数（接口4 used） */
  used: number
  /** 团队文件夹配额（接口4 cap） */
  cap: number
  /** 接口4 全档位表 tiers（拼弹窗 tab 小字/档名用，缺失回落硬编码） */
  tiers: TierInfo[]
}

/**
 * 管理「确认创建」异步流程：付费墙 → 接口 → 写回只读 + 追加任务卡 → 埋点。
 *
 * @param params - 见 UseConfirmCreateParams
 * @returns confirm 触发函数、confirming 加载态、error 错误文案（null = 无错误）
 */
export function useConfirmCreate(params: UseConfirmCreateParams): {
  confirm: () => Promise<void>
  confirming: boolean
  error: string | null
} {
  const {
    groupID,
    sessionId,
    messageId,
    folders,
    localExcludedKeys,
    paywall,
    canEdit,
    selectedCount,
    vipKind,
    used,
    cap,
    tiers
  } = params

  const dispatch = useAppDispatch()
  const payment = usePayment()
  const [confirming, setConfirming] = useState(false)
  const confirmingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const confirm = useCallback(async () => {
    if (!canEdit || !groupID || !sessionId || confirmingRef.current) return
    // 前端已判定需开通/升级（novip 非会员 / memberOver 会员超额）→ 直接弹开通窗，
    // 不调接口3（前端已知必超，调了也只会 422，白跑一次）。
    if (paywall.kind !== 'none') {
      // payToCreate 埋点：确认按钮命中商业化卡点（开通/升级会员继续）·
      // number1=本次预计创建数，与放行路径的 confirmCreate 对齐
      trackAIChatClick('payToCreate', { number1: selectedCount })
      const customContent = folderPaywallCustomContent(
        batchModalData({ vipKind, used, cap, selectedCount, tiers })
      )
      payment.openVipByKind(
        paywall.upgradeTargetVipKind,
        paywall.sourceDetail,
        false,
        customContent
      )
      return
    }
    confirmingRef.current = true
    setConfirming(true)
    setError(null)
    // confirmCreate 埋点：收口前只有 v5 PlanActionPanel 有，此处补回 live 路径缺失的
    trackAIChatClick('confirmCreate', { number1: selectedCount })
    try {
      const excludedKeys = Array.from(localExcludedKeys)
      const resp = await postConfirm(groupID, sessionId, excludedKeys)
      // 写入 card.excludedKeys + taskCreated=true · 卡片立即转 readonly
      dispatch(
        assistantFoldersHydrated({
          messageId,
          folders,
          excludedKeys,
          taskCreated: true
        })
      )
      // 追加一条独立 build_task_status message · card 仅带 taskId，运行态 +
      // 实际建夹数由 BuildTaskStatusMessage 内 useBuildTaskDetail 拉
      // /ai/chat/task 渲染（任务表 top_folders 才是真相，前端推算不准）。
      dispatch(messageAppended(makeBuildTaskStatusMessage(resp.task_id)))
    } catch (e) {
      const err = e as Error & { status?: number; body?: PaywallQuotaBody }
      const body = err.body
      // 兜底命中：前端以为额度够、服务端实际已超 → 按 422 的 used+batch_n 定位弹窗
      if (err.status === 422 && body && body.reason === 'exceeded') {
        const newTotal = (body.used ?? 0) + (body.batch_n ?? 0)
        const target = batchUpgradeTarget(newTotal, tiers)
        const customContent = folderPaywallCustomContent(
          batchModalData({
            vipKind,
            used: body.used ?? 0,
            cap: body.cap ?? cap,
            selectedCount: body.batch_n ?? 0,
            tiers
          })
        )
        payment.openVipByKind(
          target,
          batchSourceDetail(target),
          false,
          customContent
        )
      } else {
        // 含 lock_busy 与其它错误：toast（lock_busy 用后端 content），点确认创建即重试
        const msg = (body && body.content) || err.message || '确认创建失败'
        setError(msg)
        message.error(msg)
      }
    } finally {
      confirmingRef.current = false
      setConfirming(false)
    }
  }, [
    canEdit,
    groupID,
    sessionId,
    paywall,
    payment,
    localExcludedKeys,
    folders,
    dispatch,
    messageId,
    selectedCount,
    vipKind,
    used,
    cap,
    tiers
  ])

  return { confirm, confirming, error }
}
